import React, { useState } from 'react';
import { Input } from '@/components/ui/Input';

interface GeoLocationSectionProps {
  /** ISO-3166-1 alpha-2 country codes, uppercase. */
  countries: string[];
  onChange: (countries: string[]) => void;
  error?: string;
}

/** Small static list of common country codes — enough for the MVP wizard.
 *  Full geo-search (regions / cities / DMA / zip) lands later. The static
 *  list keeps the surface dependency-free; if reach is a problem the
 *  follow-up is a `/meta/geo-search` proxy mirroring `/meta/targeting-search`. */
const COMMON_COUNTRIES: Array<{ code: string; name: string }> = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'IN', name: 'India' },
  { code: 'SG', name: 'Singapore' },
];

export const GeoLocationSection: React.FC<GeoLocationSectionProps> = ({
  countries,
  onChange,
  error,
}) => {
  const [manualEntry, setManualEntry] = useState('');

  const toggleCountry = (code: string) => {
    const upper = code.toUpperCase();
    if (countries.includes(upper)) {
      onChange(countries.filter((c) => c !== upper));
    } else {
      onChange([...countries, upper]);
    }
  };

  const addManual = () => {
    const code = manualEntry.trim().toUpperCase();
    if (code.length === 2 && /^[A-Z]{2}$/.test(code) && !countries.includes(code)) {
      onChange([...countries, code]);
      setManualEntry('');
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm text-gray-600 mb-2">
          Pick the countries where this ad set can serve. At minimum one
          country is required.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {countries.map((code) => (
          <button
            key={code}
            type="button"
            onClick={() => toggleCountry(code)}
            aria-label={`Remove ${code}`}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {code} <span aria-hidden="true" className="ml-1">×</span>
          </button>
        ))}
        {countries.length === 0 && (
          <p className="text-xs text-gray-500">No countries selected.</p>
        )}
      </div>

      <div>
        <p className="text-xs font-medium text-gray-700 mb-1">
          Common countries
        </p>
        <div className="flex flex-wrap gap-1">
          {COMMON_COUNTRIES.map((c) => {
            const selected = countries.includes(c.code);
            return (
              <button
                key={c.code}
                type="button"
                onClick={() => toggleCountry(c.code)}
                aria-pressed={selected}
                className={`text-xs px-2 py-1 border rounded ${
                  selected
                    ? 'bg-blue-50 border-blue-400 text-blue-900'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {c.code} {c.name}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label
          htmlFor="meta-geo-manual"
          className="block text-xs font-medium text-gray-700 mb-1"
        >
          Add by ISO code
        </label>
        <div className="flex gap-2">
          <Input
            id="meta-geo-manual"
            type="text"
            value={manualEntry}
            onChange={(e) => setManualEntry(e.target.value)}
            placeholder="e.g. FI"
            maxLength={2}
            className="w-24 uppercase"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addManual();
              }
            }}
          />
          <button
            type="button"
            onClick={addManual}
            disabled={manualEntry.trim().length !== 2}
            className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};
